import {throwError} from 'rxjs';
import {ColumnSetting} from '../../dynamic-grid/layout.model';
import {LogService} from '../services/log.service';
import {SecurityService} from '../services/security.service';
import {VisualsService} from '../services/visuals.service';
import {GridService} from './grid.service';

export class BaseGridComponent<T extends GridService> {
  public group;
  public serviceName;

  public groupToService(group: string) {
    //get mapping of group -> service from localstorage (filled in login)
    const groupMap = this.securityService.getFromStorageAsJSON('groupServiceMap');
    if (groupMap && groupMap[group]) {
      return groupMap[group];
    }
    return group.substring(0, 1).toUpperCase() + group.slice(1) + 'Service';
  }

  //Search Form
  public continueWithSearch: boolean = true;
  public form;
  public formErrors = {};
  public validationMessages = {};
  public isCollapsed = false;
  //Form input pattern regexes
  public dateRegex =
    /(^(((0[1-9]|1[0-9]|2[0-8])[\/](0[1-9]|1[012]))|((29|30|31)[\/](0[13578]|1[02]))|((29|30)[\/](0[4,6,9]|11)))[\/](19|[2-9][0-9])\d\d$)|(^29[\/]02[\/](19|[2-9][0-9])(00|04|08|12|16|20|24|28|32|36|40|44|48|52|56|60|64|68|72|76|80|84|88|92|96)$)/;
  public intRegex = /(^0$)|(^[1-9]\d*$)/;
  public decimalRegex = /^[0-9]+(\.[0-9]{1,2})?$/;
  public viewRole = this.securityService.isOtherStaff();

  //Grid
  public count: number = 0;
  public cookieId;
  public grid;
  public footer = {};
  public header = {};
  public gridActions = [];
  public hasRowCheckbox: boolean = false;
  public checkboxPK = null;
  public checkboxPKArrayFull: Array<number> = [];
  public checkboxPKArray: Array<number> = [];
  public columnSetting: ColumnSetting[] = [];

  //Sort
  public sort: string = '';
  public sortNum: string = '1';
  public sortDir: string = 'desc';
  public secSortNum: string = '';
  public secSortDir: string = 'unsorted';

  //Pagination
  public pageSize: number = 20;
  public pageNum: number = 1;
  public pageMax: number = 0;
  public pageArray: Array<number> = [];

  //Visuals
  public visuals: any = {};
  public genericVisuals: any = {};

  //UI handlers
  public searched: boolean = false;
  public loading: boolean = false;
  public downloading: boolean = false;
  public checkboxLoading: boolean = false;

  constructor(
    public securityService: SecurityService,
    public logger: LogService,
    public visualsService: VisualsService,
    public gridService: T,
    group?: string,
  ) {
    this.group = group;
    this.pageSize = this.securityService.getPageSize();

    /*
        this.serviceName = this.gridService.constructor.name;
        this.gridService.setServiceName(this.serviceName);
        */
    // this.logger.log(this.serviceName);
  }

  /**
   ** Grid Utils
   **/

  //URLparams sanitization
  public sanitize(data: any) {
    return data != null ? data : '';
  }

  //Grid Action function handler
  public outputMethodHandler(params: any) {
    let methodName = params.methodName;
    if (this[methodName]) {
      //function exists on the component
      this[methodName](params); // call it
    }
  }

  /**
   * Visuals
   */
  public loadVisuals(): void {
    this.getGenericVisuals();
    this.getVisuals();
  }

  public getGenericVisuals() {
    this.genericVisuals = this.visualsService.getFromStorageAsJSON('visualsArray')
      ? this.visualsService.getFromStorageAsJSON('visualsArray')['generic']
      : {};
  }

  public getVisuals() {
    this.visuals = this.visualsService.getFromStorageAsJSON('visualsArray')
      ? this.visualsService.getFromStorageAsJSON('visualsArray')[this.group]
      : {};
  }

  /**
   ** Grid Search Form Functions
   **/

  /**
   * @argument formConfig:FieldConfig as emitted by @see DynamicFormComponent @function ngOnInit()
   */
  afterFormInit(formConfig: any) {
    //implement to autofill/handle form values
  }

  handleFormChanges(formConfig: any) {
    if (!this.form) {
      return;
    }
    this.form.changes.subscribe({
      next: (data) => this.onValueChanged(data),
    });
    this.getValidationMessages();
    this.afterFormInit(formConfig);
  }

  public getValidationMessages() {
    if (!this.form) {
      return;
    }
    let controls = this.form['controls'] ? this.form['controls'] : [];
    controls.forEach((field) => {
      this.formErrors[field.name] = '';
      this.validationMessages[field.name] = {};
      if (field.validation) {
        let validationAr: Array<string> = field.validation.split(',');
        validationAr.forEach((rule) => {
          if (rule === 'R') {
            this.validationMessages[field.name]['required'] = this.genericVisuals.required;
          } else if (rule === 'D') {
            this.validationMessages[field.name]['pattern'] = this.genericVisuals.dateerror;
          } else if (rule === 'I') {
            this.validationMessages[field.name]['pattern'] = this.genericVisuals.interror;
          }
        });
      }
    });
    this.onValueChanged();
  }

  onValueChanged(data?: any) {
    if (!this.form) {
      return;
    }
    const form = this.form.form;
    this.continueWithSearch = true;
    // this.logger.log(form);
    for (const field in this.formErrors) {
      // this.logger.log(field);
      // clear previous error message (if any)
      this.formErrors[field] = '';
      const control = form.get(field);

      if (control && control.dirty && !control.valid) {
        const messages = this.validationMessages[field];
        // this.logger.log(control.errors)
        for (const key in control.errors) {
          this.formErrors[field] += messages[key] + ' ';
        }
        this.continueWithSearch = false;
      }
    }
    // this.logger.log("formerrors: " + JSON.stringify(this.formErrors));
  }

  submit(value: {[name: string]: any}) {
    Object.keys(value).forEach((val) => {
      if (Array.isArray(value[val])) {
        //get value of ngx-select
        let selector = value[val][0];
        if (selector != null && selector.hasOwnProperty('id')) {
          value[val] = this.sanitize(value[val][0]['id']);
        } else {
          value[val] = '';
        }
      } else {
        value[val] = this.sanitize(value[val]);
      }
    });

    this.logger.log(value);

    this.initParams = value;
    this.search();
  }

  public search(): void {
    this.loading = true;
    this.pageNum = 1;
    this.getInitData();
  }

  /**
   ** All standard Grid functions
   **/

  /**
   *  Grid Data serialized call hierarchy
   */

  /**
   * can be implemented to handle data returned by
   * @see getData()
   */
  public getDataCallback() {}

  public initParams = {};
  public initDataParams(): any {
    //autofilled by submit(...)
    return this.initParams;
  }
  public getInitData(): void {
    if (this.continueWithSearch) {
      this.loading = true;
      this.searched = true;
      this.gridService.initData(this.initDataParams()).subscribe({
        next: (data) => {
          this.cookieId = data;
          this.logger.log(this.cookieId);
          this.getColumnDefinition();
          this.getGridActions();
        },
        error: (error) => {
          this.loading = false;
          return throwError(() => new Error(error));
        },
      });
    }
  }

  public gridActionsParams(): any {
    return {
      group: this.group,
    };
  }
  public getGridActions(): void {
    if (this.viewRole) {
      this.gridActions = [];
      this.hasRowCheckbox = false;
    } else {
      this.gridService.getGridActions(this.gridActionsParams()).subscribe({
        next: (data) => {
          if (data) this.gridActions = data;

          if (this.gridActions) {
            this.gridActions.forEach((element) => {
              if (element['method'] == 'selectAll') {
                this.hasRowCheckbox = element['hidden'] ? element['hidden'] : false;
              }
            });
          }
        },
        error: (error) => {
          this.loading = false;
          return throwError(() => new Error(error));
        },
      });
    }
  }

  public columnDefinitionParams(): any {
    return {
      group: this.group,
    };
  }
  public getColumnDefinition(): void {
    this.gridService.getColumnDefinition(this.columnDefinitionParams()).subscribe({
      next: (data) => {
        this.columnSetting = data;
        this.getDataCount();
      },
      error: (error) => {
        this.loading = false;
        return throwError(() => new Error(error));
      },
    });
  }

  public dataCountParams(): any {
    return {
      cookieId: this.cookieId,
    };
  }
  public getDataCount(): void {
    this.gridService.getDataCount(this.dataCountParams()).subscribe({
      next: (data) => {
        this.count = data;
        if (this.count > 0) {
          this.calcPages();
          this.getHeaderData();
          this.getFooterData();
          this.getData();
        } else {
          this.loading = false;
        }
      },
      error: (error) => {
        this.loading = false;
        this.count = 0;
        return throwError(() => new Error(error));
      },
    });
  }

  public dataParams(): any {
    let firstRow = 1 + this.pageSize * (this.pageNum - 1);
    let lastRow = firstRow + this.pageSize - 1;

    return {
      cookieId: this.cookieId,
      start: firstRow,
      end: lastRow,
      order: this.sort,
    };
  }
  public getData(): void {
    this.gridService.getData(this.dataParams()).subscribe({
      next: (data) => {
        this.grid = data;
        this.dataCheckboxInit();
        this.getDataCallback();
        this.loading = false;
      },
      error: (error) => {
        this.loading = false;
        return throwError(() => new Error(error));
      },
    });
  }

  public footerDataParams(): any {
    return {
      cookieId: this.cookieId,
    };
  }
  public getFooterData(): void {
    this.gridService.getFooter(this.footerDataParams()).subscribe({
      next: (data) => {
        if (data) this.footer = data;
      },
      error: (error) => {
        return throwError(() => new Error(error));
      },
    });
  }

  public headerDataParams(): any {
    return {
      cookieId: this.cookieId,
    };
  }
  public getHeaderData(): void {
    this.gridService.getHeader(this.headerDataParams()).subscribe({
      next: (data) => {
        if (data) this.header = data;
      },
      error: (error) => {
        return throwError(() => new Error(error));
      },
    });
  }

  public txtParams(): any {
    return {
      cookieId: this.cookieId,
      order: this.sort,
    };
  }
  public getTxt() {
    this.downloading = true;
    this.gridService.getTxt(this.txtParams()).subscribe({
      next: () => {
        this.logger.log('downloading the file');
        this.downloading = false;
      },
      error: (error) => {
        this.logger.error(error);
        this.downloading = false;
      },
    });
  }

  protected csvParams(): any {
    return {
      cookieId: this.cookieId,
      order: this.sort,
    };
  }
  protected getCsv() {
    this.downloading = true;
    this.gridService.getCsv(this.csvParams()).subscribe({
      next: () => {
        this.logger.log('downloading the file');
        this.downloading = false;
      },
      error: (error) => {
        this.logger.error(error);
        this.downloading = false;
      },
    });
  }

  protected xlsParams(): any {
    return {
      cookieId: this.cookieId,
      order: this.sort,
    };
  }
  protected getXls() {
    this.downloading = true;
    this.gridService.getXls(this.xlsParams()).subscribe({
      next: (data) => {
        this.logger.log('downloading the file');
        this.downloading = false;
      },
      error: (error) => {
        this.logger.error(error);
        this.downloading = false;
      },
    });
  }

  public pdfParams(): any {
    return {
      cookieId: this.cookieId,
      order: this.sort,
    };
  }
  public getPdf() {
    this.downloading = true;
    this.gridService.getPdf(this.pdfParams()).subscribe({
      next: (data) => {
        this.logger.log('downloading the file');
        this.downloading = false;
      },
      error: (error) => {
        this.logger.error(error);
        this.downloading = false;
      },
    });
  }

  /**
   * Grid Sorting
   */
  public changeSort(num: any): void {
    /*
        let sortNum;
        if (!(typeof num === 'number' && (num%1) === 0) && num.toString().match(/^[1-9]\d*$/)){
            sortNum=  Number(num);
        }else if(typeof num === 'number' && (num%1) === 0){
            sortNum = num
        }
        */
    this.loading = true;

    switch (num.toString()){
      case this.sortNum:
        this.sortDir = this.sortDir == 'desc' ? 'asc': 'desc';
        break;
      case this.secSortNum:
        this.sortNum = this.secSortNum;
        this.sortDir = this.sortDir == 'desc' ? 'asc': 'desc';
        break;
      default:
        this.sortNum = num.toString();
        this.sortDir = 'asc';
        break;
    }   

    //clear secondary sorting because after change sort only 1 column can be sorted
    this.secSortNum = '';
    this.secSortDir = 'unsorted';

    this.sort = this.sortNum + ' ' + this.sortDir;
    this.getDataCount();
  }

  public getSortState(column) {
    return this.sortNum == column ? (this.sortDir == 'asc' ? 'sortasc' : 'sortdesc') : 'unsorted';
  }

  /**
   * Grid Pagination
   */
  public changePageSize(num: any): void {
    this.loading = true;
    this.pageSize = num;
    this.pageNum = 1;
    this.getDataCount();
  }

  public changePage(obj: any): void {
    const num = obj['num'];
    let type = obj['type'];

    if (!(typeof num === 'number' && num % 1 === 0) && num.toString().match(/^[1-9]\d*$/)) {
      this.loading = true;
      let number = Number(num);
      this.changePageType(number, type);
      this.getDataCount();
    } else if (typeof num === 'number' && num % 1 === 0) {
      this.loading = true;
      this.changePageType(num, type);
      this.getDataCount();
    }
  }

  public changePageType(num: number, type: string): void {
    if (type == 'full') {
      if (num > this.pageMax) {
        this.pageNum = this.pageMax;
      } else {
        this.pageNum = num;
      }
    } else if (type == 'next') {
      this.pageNum = num + 1;
    } else if (type == 'prev') {
      this.pageNum = num - 1;
    }
  }

  public calcPages(): void {
    this.pageMax = Math.ceil(this.count / this.pageSize);
    for (let i = 0; i < this.pageMax; i++) {
      this.pageArray.push(i + 1);
    }
  }

  /**
   * Grid checkboxes
   */

  public getCheckboxCallback() {}

  public dataCheckboxInit() {}

  public filterCheckboxParams(params): any {
    if (this.checkboxPK) {
      let PK = {};
      if (Array.isArray(this.checkboxPK)) {
        this.checkboxPK.forEach((element) => {
          PK[element] = params[element];
        });
      } else if (typeof this.checkboxPK === 'string') {
        PK[this.checkboxPK] = params[this.checkboxPK];
      } else {
        return params;
      }
      return PK;
    } else {
      return params;
    }
  }

  /**
   * Grid checkbox toggling
   */
  public checkboxToggleParams(): any {
    //passes whole (row)Object:any by default, lets server handle used params(PK)
    //override any of its properties here
    return {
      cookieId: this.cookieId,
    };
  }
  public checkToggle(params) {
    this.checkboxLoading = true;
    this.searched = true;
    params.columnMaps = undefined;
    if (params['selected'] != null && params['selected'] != '') {
      if (params['selected'] == 'Y') {
        this.gridService
          .deselect(
            Object.assign({}, this.filterCheckboxParams(params), this.checkboxToggleParams()),
          )
          .subscribe({
            next: (data) => {
              this.logger.log('deselect ' + data);
              this.getData();
            },
            error: (error) => {
              this.checkboxLoading = false;
              return throwError(() => new Error(error));
            },
          });
      } else {
        this.gridService
          .select(Object.assign({}, this.filterCheckboxParams(params), this.checkboxToggleParams()))
          .subscribe({
            next: (data) => {
              this.logger.log('select ' + data);
              this.getData();
            },
            error: (error) => {
              this.checkboxLoading = false;
              return throwError(() => new Error(error));
            },
          });
      }
    } else {
      this.logger.error('Object property "selected" missing.');
    }
  }

  public checkboxAllParams(): any {
    return {
      cookieId: this.cookieId,
    };
  }
  public selectAll(params) {
    this.checkboxLoading = true;
    this.searched = true;
    this.gridService.selectAll(this.checkboxAllParams()).subscribe({
      next: (data) => {
        this.logger.log('selectAll ' + data);
        this.getData();
      },
      error: (error) => {
        this.checkboxLoading = false;
        return throwError(() => new Error(error));
      },
    });
  }

  public deselectAll(params) {
    this.checkboxLoading = true;
    this.searched = true;
    this.gridService.deselectAll(this.checkboxAllParams()).subscribe({
      next: (data) => {
        this.logger.log('deselectAll ' + data);
        this.getData();
      },
      error: (error) => {
        this.checkboxLoading = false;
        return throwError(() => new Error(error));
      },
    });
  }

  isIterable(obj) {
    // checks for null and undefined
    if (obj == null) {
      return false;
    }
    return typeof obj[Symbol.iterator] === 'function';
  }

  mapFields(fields: any[]): {fields: any[]; options: any} {
    let formState: any = {};
    let formlyFields = fields.map((fl) => {
      fl.label = this.visuals[fl.label];
      fl.fields.map((el) => {
        this.mapFieldGroups(el, formState);
      });
      return fl;
    });

    let formlyOptions: any = {
      formState: formState, // Populate formState here
    };

    return {fields: formlyFields, options: formlyOptions};
  }

  mapFieldGroups(fieldArray: any, formState: any) {
    if (fieldArray.hasOwnProperty('fieldGroup')) {
      fieldArray['fieldGroup'].map((fieldGroup) => {
        if (fieldGroup.hasOwnProperty('fieldGroup')) {
          fieldGroup['fieldGroup'].map((nestedFieldGroup) => {
            this.templateOptionsMapping(nestedFieldGroup, formState);
            if (nestedFieldGroup.hasOwnProperty('fieldArray')) {
              this.mapFieldGroups(nestedFieldGroup['fieldGroup'], formState);
            }
          });
        } else {
          this.templateOptionsMapping(fieldGroup, formState);
          if (fieldGroup.hasOwnProperty('fieldArray')) {
            this.mapFieldGroups(fieldGroup['fieldArray'], formState);
          }
        }
      });
    }
  }

  templateOptionsMapping(formlyOption: any, formState: any): any {
    formlyOption.props = {...formlyOption.templateOptions};
    if (formlyOption.templateOptions && formlyOption.templateOptions.options) {
      if (formlyOption.templateOptions.isnotLKP) {
        formlyOption.props.options = this[formlyOption.templateOptions.options];
      } else {
        formlyOption.props.options = this.visualsService.getLkpFromStorageAsJSON(
          formlyOption.templateOptions.options,
          true,
        );
      }
    }
    if (formlyOption.templateOptions) {
      if (formlyOption.expressionProperties)
        Object.keys(formlyOption.expressionProperties).forEach((key) => {
          if (key.includes('templateOptions')) {
            formlyOption.expressionProperties[key.replace('templateOptions', 'props')] =
              formlyOption.expressionProperties[key];
            delete formlyOption.expressionProperties[key];
          }
        });
      formlyOption.props.label = formlyOption.key
        ? this.visuals[formlyOption.key]
        : this.visuals[formlyOption.props.label];
      formlyOption.props.innerLabel = this.visuals[formlyOption.props.innerLabel];
      formlyOption.props.addButton = this.visuals[formlyOption.props.addButton];
      if (formlyOption.templateOptions.fourEyesValue) {
        formState[formlyOption.key] = {
          fourEyesValue: formlyOption.templateOptions.fourEyesValue,
        };
      }
    }
    if (formlyOption.props && formlyOption.props.change) {
      formlyOption.props.change = Function('$index', '$event', formlyOption.props.change).bind(
        this,
      );
    }
    if (formlyOption.props && formlyOption.props.onClick) {
      formlyOption.props.onClick = Function('field', '$event', formlyOption.props.onClick).bind(
        this,
      );
    }
    delete formlyOption.templateOptions;
  }
}
